/* ============================================================
   Админка · Турниры: таблица + редактор с табами
   (Информация / Заявка / Группы / Сетка / Участники).

   Весь редактор работает с ОДНИМ черновиком: «Сохранить» публикует,
   «Отмена» действительно отменяет. Раньше вкладки Заявка/Группы/
   Сетка/Участники писали на сервер на каждое нажатие клавиши.

   У турниров с живой сеткой (hasData: 'mlbb' / 'team') команды, заявки
   и состав управляются здесь через fed-teams. Ручное управление матчами
   доступно во вкладке «Сетка» и на странице турнира.
   ============================================================ */
import { useEffect, useMemo, useState } from 'react';
import { useFedContent, isSeededTour, uploadUrl } from '../content';
import { API_URL, apiFetch, authHeaders, useAppStore } from '../../store';
import { Ic } from '../icons';
import { Seg } from '../ui';
import { tournamentPhase } from '../data';
import { AdmCard, StPill, CoverSwatch, AdmIconBtn, AdmPrimaryBtn, EmptyRow, ViewOnSiteBtn } from './ui';
import TournamentQrModal from '../components/TournamentQr';
import MlbbBracket from '../mlbb/Bracket';
import { useMlbb } from '../mlbb/store';
import NovaBracket from '../mlbb/NovaBracket';
import { hasNovaGroups, isDotaTournament, buildNovaBracket } from '../mlbb/novaBracketEngine';
import GroupDraw from './GroupDraw';

const ST_OPTIONS = [['soon', 'Анонс'], ['registration', 'Регистрация открыта'], ['closed', 'Регистрация закрыта'], ['live', 'Идёт'], ['done', 'Завершён']];
const BASIC_ST_OPTIONS = ST_OPTIONS.filter(([value]) => !['registration', 'closed'].includes(value));

const uid = () => Math.random().toString(36).slice(2, 9);

const isLive = (t) => t && (t.hasData === 'mlbb' || t.hasData === 'team');

export function AdmTournamentsList({ onEdit, regs = [] }) {
  const tournaments = useFedContent((s) => s.tournaments);
  const deleteTournament = useFedContent((s) => s.deleteTournament);
  const fire = useAppStore((s) => s.fire);

  // Сколько заявок реально подано на турнир — раньше в колонке стояло
  // введённое руками число из карточки, не связанное с заявками
  const regCount = (id) => regs.filter((r) => r.tournamentKey === id).length;

  const remove = async (t) => {
    if (!window.confirm(`Удалить турнир «${t.title}»?`)) return;
    const ok = await deleteTournament(t.id);
    if (ok) fire && fire('Турнир удалён', 'ok');
  };

  return (
    <div className="fed-admin-view fed-tour-list">
      <AdmCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="fed-tbl">
            <thead>
              <tr>
                <th>Турнир</th><th>Дисциплина</th><th>Статус</th><th>Заявок</th><th>Дата</th><th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t) => (
                <tr key={t.id} className={`fed-list-showcase-card fed-tour-showcase-card ${isLive(t) ? 'is-live' : ''}`}>
                  <td data-wide className="fed-showcase-hero-cell">
                    <span className="fed-showcase-menu" aria-hidden="true"><Ic name="more" size={19} /></span>
                    <div className="fed-showcase-hero flex items-center gap-3">
                      <span className="fed-showcase-emblem fed-tour-emblem"><Ic name="trophy" size={36} sw={1.8} /></span>
                      <CoverSwatch hue={t.hue} className="fed-showcase-cover-desktop" />
                      <div className="min-w-0">
                        <div className="fed-showcase-title font-semibold text-fg flex items-center gap-2" style={{ fontSize: 14 }}>
                          {t.title}
                          {isLive(t) && (
                            <span className="font-mono uppercase rounded" style={{ fontSize: 8.5, padding: '1px 5px', letterSpacing: '.08em', color: '#c084fc', background: 'rgba(168,85,247,.16)' }}>
                              живая сетка
                            </span>
                          )}
                        </div>
                        <div className="fed-showcase-subtitle font-mono text-fg-faint" style={{ fontSize: 10.5, marginTop: 2 }}>{t.game}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Дисциплина"><span className="font-mono text-fg-soft bg-surface-3 rounded-md" style={{ fontSize: 11, padding: '4px 8px' }}>{t.disc}</span></td>
                  <td data-label="Статус"><StPill st={tournamentPhase(t)} /></td>
                  <td data-label="Заявок" className="font-mono">{regCount(t.id) || '—'}</td>
                  <td data-label="Дата" style={{ whiteSpace: 'nowrap' }}>{t.date_ru || '—'}</td>
                  <td>
                    <div className="fed-tour-actions-desktop flex gap-2">
                      <ViewOnSiteBtn route={`tournament/${t.id}`} title="Смотреть страницу" />
                      <AdmIconBtn name="edit" title="Редактировать" onClick={() => onEdit(t.id)} />
                      {!isSeededTour(t.id) && (
                        <AdmIconBtn name="trash" tint="danger" title="Удалить" onClick={() => remove(t)} />
                      )}
                    </div>
                    <div className="fed-tour-actions-mobile">
                      <a href={`/#tournament/${t.id}`} target="_blank" rel="noopener noreferrer" className="fed-tour-bigbtn ghost">
                        <Ic name="external" size={17} /> К турниру
                      </a>
                      <button onClick={() => onEdit(t.id)} className="fed-tour-bigbtn primary">
                        <Ic name="edit" size={17} /> Изменить
                      </button>
                      {!isSeededTour(t.id) && (
                        <button onClick={() => remove(t)} className="fed-tour-delete-mobile" aria-label="Удалить турнир">
                          <Ic name="trash" size={17} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!tournaments.length && <EmptyRow colSpan={6}>Турниров нет — создайте первый</EmptyRow>}
            </tbody>
          </table>
        </div>
      </AdmCard>
    </div>
  );
}

/* ---------- редактор ---------- */

// обычная функция (не компонент) — чтобы инпуты не пересоздавались и не теряли фокус
const field = (label, children) => (
  <label key={label} className="flex flex-col gap-[7px]">
    <span className="fed-label">{label}</span>
    {children}
  </label>
);

function InfoTab({ draft, setDraft }) {
  const docs = useFedContent((s) => s.docs);
  const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  const uploadImage = useFedContent((s) => s.uploadImage);
  const fire = useAppStore((s) => s.fire);
  const [uploading, setUploading] = useState(false);
  const uploadPoster = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setDraft({ ...draft, image: uploadUrl(await uploadImage(file)) }); }
    catch (error) { fire(error.message || 'Не удалось загрузить афишу', 'error'); }
    finally { setUploading(false); }
  };
  const live = isLive(draft);
  const phaseOptions = live ? ST_OPTIONS : BASIC_ST_OPTIONS;
  const phaseValue = live ? tournamentPhase(draft) : (draft.status || 'soon');
  const changePhase = (e) => {
    const phase = e.target.value;
    const next = {
      ...draft,
      status: phase === 'live' || phase === 'done' ? phase : 'soon',
      regOpen: phase === 'registration',
      registrationClosed: phase === 'closed' || phase === 'live' || phase === 'done',
      ...(phase === 'registration' ? { registrationOpensAt: '' } : {}),
    };
    if (phase === 'done') next.matchesOpen = false;
    setDraft(next);
  };
  const [kz, setKz] = useState(false);

  return (
    <div className="fed-tour-info flex flex-col gap-4">
      <AdmCard className="fed-tour-edit-card" style={{ padding: 24 }}>
        <div className="fed-grid-form">
          {field('Название', <input className="fed-input" value={draft.title || ''} onChange={upd('title')} />)}
          {field('Дисциплина', <input className="fed-input" value={draft.disc || ''} onChange={upd('disc')} />)}
          {field('Игра', <input className="fed-input" value={draft.game || ''} onChange={upd('game')} />)}
          {field('Статус',
            <select className="fed-input" value={phaseValue} onChange={changePhase}>
              {phaseOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
          {field('Дата', <input className="fed-input" value={draft.date_ru || ''} onChange={upd('date_ru')} placeholder="19 июля 2026, 20:00 — можно оставить пустым" />)}
          {field('Площадка', <input className="fed-input" value={draft.place_ru || ''} onChange={upd('place_ru')} />)}
          {field('Формат', <input className="fed-input" value={draft.format_ru || ''} onChange={upd('format_ru')} />)}
          {field('Участников (число на карточке)', <input className="fed-input" type="number" value={draft.players || 0} onChange={(e) => setDraft({ ...draft, players: parseInt(e.target.value || '0', 10) })} />)}
          {field('Платформа', <input className="fed-input" value={draft.platform || ''} onChange={upd('platform')} />)}
          {field('Призовой фонд', <input className="fed-input" value={draft.prize_ru || ''} onChange={upd('prize_ru')} />)}
          {field('Афиша: ссылка на изображение', <input className="fed-input" value={draft.image || ''} onChange={upd('image')} placeholder="Можно добавить позднее" />)}
          {field(uploading ? 'Загрузка афиши…' : 'Загрузить афишу', <input className="fed-input" type="file" accept="image/*" disabled={uploading} onChange={uploadPoster} />)}
          {field('Документ регламента (из раздела «Документы»)',
            <select className="fed-input" value={draft.regulationDocId || ''} onChange={upd('regulationDocId')}>
              <option value="">Без документа</option>
              {docs.map((doc) => <option key={doc.id} value={doc.id}>{doc.name_ru}</option>)}
            </select>
          )}
          {draft.status === 'done' && field('Чемпион (команда / сборная)', <input className="fed-input" value={draft.champ || ''} onChange={upd('champ')} />)}
          {draft.status === 'done' && field('Игрок-чемпион', <input className="fed-input" value={draft.champPlayer || ''} onChange={upd('champPlayer')} />)}
          {draft.status === 'done' && field('Код флага чемпиона (напр. ba, kr — пусто, если без флага)', <input className="fed-input" value={draft.champCc || ''} onChange={upd('champCc')} />)}
        </div>
        <label className="flex flex-col gap-[7px]" style={{ marginTop: 16 }}>
          <span className="fed-label">Описание</span>
          <textarea className="fed-input" rows={4} value={draft.about_ru || ''} onChange={upd('about_ru')} />
        </label>
      </AdmCard>

      {/* ===== казахская версия ===== */}
      <AdmCard className="fed-tour-edit-card" style={{ padding: 18 }}>
        <button
          onClick={() => setKz(!kz)}
          className="flex w-full items-center gap-2.5 bg-transparent border-none cursor-pointer text-left"
        >
          <span style={{ display: 'inline-flex', transform: kz ? 'rotate(90deg)' : 'none', transition: 'transform .18s', color: 'var(--fed-fg-muted)' }}>
            <Ic name="chevR" size={16} />
          </span>
          <span className="font-display font-semibold uppercase text-fg" style={{ fontSize: 14 }}>Казахская версия</span>
          <span className="text-fg-faint" style={{ fontSize: 12 }}>— пустые поля покажутся по-русски</span>
        </button>
        {kz && (
          <div className="fed-grid-form" style={{ marginTop: 16 }}>
            {field('Атауы (қаз)', <input className="fed-input" value={draft.title_kz || ''} onChange={upd('title_kz')} />)}
            {field('Күні (қаз)', <input className="fed-input" value={draft.date_kz || ''} onChange={upd('date_kz')} />)}
            {field('Алаң (қаз)', <input className="fed-input" value={draft.place_kz || ''} onChange={upd('place_kz')} />)}
            {field('Формат (қаз)', <input className="fed-input" value={draft.format_kz || ''} onChange={upd('format_kz')} />)}
            {field('Жүлде қоры (қаз)', <input className="fed-input" value={draft.prize_kz || ''} onChange={upd('prize_kz')} />)}
            <label className="flex flex-col gap-[7px]" style={{ gridColumn: '1 / -1' }}>
              <span className="fed-label">Сипаттама (қаз)</span>
              <textarea className="fed-input" rows={4} value={draft.about_kz || ''} onChange={upd('about_kz')} />
            </label>
          </div>
        )}
      </AdmCard>
    </div>
  );
}

/* ---------- живой турнир (MLBB) ---------- */

function Toggle({ on, onChange, title, hint }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!on}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: 'var(--fed-accent)', marginTop: 2 }}
      />
      <span className="flex flex-col">
        <span className="font-display font-semibold uppercase text-fg" style={{ fontSize: 14 }}>{title}</span>
        <span className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 3, lineHeight: 1.5 }}>{hint}</span>
      </span>
    </label>
  );
}

function LiveTab({ draft, setDraft }) {
  const upd = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
  return (
    <div className="fed-admin-view flex flex-col gap-4">
      <AdmCard style={{ padding: 20 }} className="flex flex-col gap-4">
        <Toggle
          on={draft.regOpen === true}
          onChange={(v) => setDraft({ ...draft, status: 'soon', regOpen: v, registrationClosed: !v })}
          title="Приём заявок включён"
          hint="При заданном времени открытия регистрация начнётся автоматически. Закрытие сохраняет уже поданные заявки."
        />
        {field('Автоматическое открытие регистрации · время Казахстана (UTC+5)', <input
          className="fed-input" type="datetime-local"
          value={Number.isFinite(Date.parse(draft.registrationOpensAt)) ? new Date(Date.parse(draft.registrationOpensAt) + 5 * 3600000).toISOString().slice(0, 16) : ''}
          onChange={(event) => setDraft({ ...draft, registrationOpensAt: event.target.value ? `${event.target.value}:00+05:00` : '', ...(event.target.value ? { status: 'soon', regOpen: true, registrationClosed: false } : {}) })}
        />)}
        <div className="border-t border-line" />
        <Toggle
          on={draft.matchesOpen === true}
          onChange={(v) => setDraft({ ...draft, matchesOpen: v })}
          title="Матчи открыты"
          hint="Пока выключено, сетку видит только администратор: команды не могут отмечать готовность и присылать результаты."
        />
      </AdmCard>

      <AdmCard style={{ padding: 24 }}>
        <div className="fed-grid-form">
          {field('Слотов (сколько команд проходит дальше)', <input className="fed-input" type="number" value={draft.slots || 0} onChange={(e) => setDraft({ ...draft, slots: parseInt(e.target.value || '0', 10) })} />)}
          {field('Призовой фонд лиги', <input className="fed-input" value={draft.prizeUsd || ''} onChange={upd('prizeUsd')} placeholder="5 000 $" />)}
          {field('Закрытие регистрации (рус)', <input className="fed-input" value={draft.regClose_ru || ''} onChange={upd('regClose_ru')} placeholder="до 19 июля, 18:00" />)}
          {field('Закрытие регистрации (қаз)', <input className="fed-input" value={draft.regClose_kz || ''} onChange={upd('regClose_kz')} />)}
          {field('Организатор', <input className="fed-input" value={draft.org || ''} onChange={upd('org')} />)}
          {field('Пост в Telegram', <input className="fed-input" value={draft.tgPost || ''} onChange={upd('tgPost')} placeholder="https://t.me/…" />)}
          {field('Сезон', <input className="fed-input" value={draft.season || ''} onChange={upd('season')} />)}
          {field('Номер квалификации', <input className="fed-input" type="number" value={draft.qualifierNo || 0} onChange={(e) => setDraft({ ...draft, qualifierNo: parseInt(e.target.value || '0', 10) })} />)}
        </div>
      </AdmCard>
    </div>
  );
}

const liveGame = (tour) => {
  if (tour.teamGame) return String(tour.teamGame).toLowerCase();
  if (tour.hasData === 'mlbb') return 'mlbb';
  const discipline = `${tour.disc || ''} ${tour.game || ''}`.toLowerCase();
  if (discipline.includes('counter') || discipline.includes('cs2')) return 'cs2';
  if (discipline.includes('dota')) return 'dota2';
  return String(tour.disc || '').toLowerCase();
};

const TEAM_ERROR = {
  'not-found': 'Команда больше не существует',
  'not-admin': 'Для этого действия нужны права администратора',
  'fill-all': 'Заполните название и тег команды',
  'tag-too-long': 'Тег должен быть не длиннее 10 символов',
  'need5': 'Для заявки нужно минимум 5 участников',
  'wrong-game': 'Команда относится к другой дисциплине',
  'tour-started': 'Матчи уже начались: изменение заявки пересоберёт посев сетки',
};

/** Управление реальными командами командного турнира прямо из админки. */
function LiveTeamsAdmin({ tour, onChanged }) {
  const fire = useAppStore((s) => s.fire);
  const game = liveGame(tour);
  const [state, setState] = useState({ teams: [], regs: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [onlyRegistered, setOnlyRegistered] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', tag: '' });

  const endpoint = (path) => `${API_URL}/fed-teams${path}`;

  const readJson = async (res) => {
    let data = {};
    try { data = await res.json(); } catch (e) { /* пустой ответ */ }
    if (!res.ok) {
      const err = new Error(TEAM_ERROR[data.error] || data.error || `HTTP ${res.status}`);
      err.code = data.error;
      err.status = res.status;
      throw err;
    }
    return data;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tour: tour.id, game });
      const res = await apiFetch(endpoint(`/state?${params}`), { headers: authHeaders(false) });
      setState(await readJson(res));
    } catch (err) {
      fire && fire(err.message || 'Не удалось загрузить команды', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tour.id, game]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = async (key, path, { method = 'POST', body, force = false } = {}) => {
    setBusy(key);
    try {
      const join = path.includes('?') ? '&' : '?';
      const url = endpoint(`${path}${force ? `${join}force=1` : ''}`);
      const res = await apiFetch(url, {
        method,
        headers: authHeaders(body !== undefined),
        body: body !== undefined ? JSON.stringify({ tour: tour.id, game, ...body }) : undefined,
      });
      const next = await readJson(res);
      setState(next);
      onChanged && onChanged();
      return true;
    } catch (err) {
      if (err.code === 'tour-started' && !force) {
        const ok = window.confirm(
          'Матчи уже начались. Принудительное действие изменит посев и может сделать сохранённые результаты неверными. Продолжить?'
        );
        if (ok) return mutate(key, path, { method, body, force: true });
      } else {
        fire && fire(err.message || 'Не удалось выполнить действие', 'error');
      }
      return false;
    } finally {
      setBusy('');
    }
  };

  const registered = useMemo(
    () => new Set((state.regs || []).filter((r) => r.tourId === tour.id).map((r) => r.teamId)),
    [state.regs, tour.id]
  );
  const teams = useMemo(() => {
    const rows = [...(state.teams || [])].sort((a, b) => Number(registered.has(b.id)) - Number(registered.has(a.id)));
    return onlyRegistered ? rows.filter((team) => registered.has(team.id)) : rows;
  }, [state.teams, registered, onlyRegistered]);

  const startEdit = (team) => {
    setEditing(team.id);
    setEditDraft({ name: team.name || '', tag: team.tag || '' });
  };

  const saveTeam = async (team) => {
    const name = editDraft.name.trim();
    const tag = editDraft.tag.trim().toUpperCase();
    if (!name || !tag) { fire && fire('Заполните название и тег команды', 'error'); return; }
    const ok = await mutate(`edit:${team.id}`, `/teams/${encodeURIComponent(team.id)}`, {
      method: 'PUT', body: { name, tag },
    });
    if (ok) {
      setEditing(null);
      fire && fire('Команда обновлена', 'ok');
    }
  };

  const toggleRegistration = async (team) => {
    const isRegistered = registered.has(team.id);
    if (isRegistered && !window.confirm(`Снять команду «${team.name}» с этого турнира? Сама команда и её состав останутся.`)) return;
    const action = isRegistered ? 'unregister' : 'register';
    const ok = await mutate(`${action}:${team.id}`, `/teams/${encodeURIComponent(team.id)}/${action}`, {
      body: {},
    });
    if (ok) fire && fire(isRegistered ? 'Заявка команды снята' : 'Команда добавлена в турнир', 'ok');
  };

  const removeMember = async (team, member) => {
    if (member.key === team.captain) return;
    if (!window.confirm(`Удалить игрока ${member.nick || member.key} из команды «${team.name}»?`)) return;
    const params = new URLSearchParams({ tour: tour.id, game });
    const ok = await mutate(
      `member:${team.id}:${member.key}`,
      `/teams/${encodeURIComponent(team.id)}/members/${encodeURIComponent(member.key)}?${params}`,
      { method: 'DELETE' }
    );
    if (ok) fire && fire('Игрок удалён из состава', 'ok');
  };

  const deleteTeam = async (team) => {
    if (!window.confirm(
      `Полностью удалить команду «${team.name}»? Будут удалены её заявки во ВСЕ турниры и состав. Если нужно убрать её только отсюда, используйте «Снять заявку».`
    )) return;
    const params = new URLSearchParams({ tour: tour.id, game });
    const ok = await mutate(`delete:${team.id}`, `/teams/${encodeURIComponent(team.id)}?${params}`, { method: 'DELETE' });
    if (ok) fire && fire('Команда полностью удалена', 'ok');
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      <AdmCard style={{ padding: 18 }} className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-semibold uppercase text-fg" style={{ fontSize: 15 }}>Команды турнира</div>
          <div className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            Заявок: {registered.size} · команд в дисциплине: {(state.teams || []).length}
          </div>
        </div>
        <label className="flex items-center gap-2 text-fg-soft cursor-pointer" style={{ fontSize: 12.5 }}>
          <input type="checkbox" checked={onlyRegistered} onChange={(e) => setOnlyRegistered(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--fed-accent)' }} />
          Только участники турнира
        </label>
        <button className="fed-btn-ghost h-9 px-3.5 rounded-[10px]" onClick={load} disabled={loading}>
          Обновить
        </button>
      </AdmCard>

      {loading ? (
        <AdmCard style={{ padding: 28 }} className="text-center text-fg-muted">Загружаем команды…</AdmCard>
      ) : teams.map((team) => {
        const isRegistered = registered.has(team.id);
        const isEditing = editing === team.id;
        return (
          <AdmCard key={team.id} style={{ padding: 18 }} className="flex flex-col gap-4">
            <div className="flex items-start gap-3 flex-wrap">
              <span className="flex items-center justify-center rounded-xl font-display font-bold text-fg" style={{ width: 48, height: 48, background: 'var(--fed-surface-3)', border: '1px solid var(--fed-line-2)', fontSize: 13 }}>
                {team.tag || '—'}
              </span>
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold uppercase text-fg" style={{ fontSize: 16 }}>{team.name}</span>
                  <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>#{team.id}</span>
                  <StPill st={isRegistered ? 'approved' : 'new'} label={isRegistered ? 'В турнире' : 'Не заявлена'} />
                </div>
                <div className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 5 }}>Состав: {(team.members || []).length}/7</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button className="fed-btn-ghost h-9 px-3 rounded-[9px]" onClick={() => startEdit(team)} disabled={!!busy}>
                  <Ic name="edit" size={14} /> Изменить
                </button>
                <button className="fed-btn-ghost h-9 px-3 rounded-[9px]" onClick={() => toggleRegistration(team)} disabled={!!busy}>
                  {busy === `${isRegistered ? 'unregister' : 'register'}:${team.id}` ? 'Сохраняем…' : isRegistered ? 'Снять заявку' : 'Добавить в турнир'}
                </button>
                <button className="fed-iconbtn" style={{ color: 'var(--fed-accent-bright)', borderColor: 'var(--fed-line-accent)' }} onClick={() => deleteTeam(team)} disabled={!!busy} title="Полностью удалить команду">
                  <Ic name="trash" size={15} />
                </button>
              </div>
            </div>

            {isEditing && (
              <div className="grid gap-2.5 border-t border-line" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', paddingTop: 14 }}>
                <input className="fed-input" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} placeholder="Название команды" />
                <input className="fed-input" value={editDraft.tag} maxLength={10} onChange={(e) => setEditDraft({ ...editDraft, tag: e.target.value.toUpperCase() })} placeholder="Тег" />
                <div className="flex gap-2">
                  <button className="fed-btn-ghost h-10 px-3 rounded-[9px]" onClick={() => setEditing(null)}>Отмена</button>
                  <button className="fed-btn-primary h-10 px-3 rounded-[9px]" onClick={() => saveTeam(team)} disabled={!!busy}>Сохранить</button>
                </div>
              </div>
            )}

            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
              {(team.members || []).map((member, index) => {
                const captain = member.key === team.captain;
                return (
                  <div key={member.key} className="flex items-center gap-2.5 rounded-[10px] border border-line bg-surface" style={{ padding: '9px 10px' }}>
                    <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{index + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-fg truncate" style={{ fontSize: 13 }}>{member.nick || 'Без ника'} {captain && <span className="text-gold">· капитан</span>}</div>
                      <div className="font-mono text-fg-faint truncate" style={{ fontSize: 9.5, marginTop: 2 }}>{member.gameId || 'ID не указан'} · {member.tg || 'контакт не указан'}</div>
                    </div>
                    <button
                      className="fed-iconbtn"
                      style={{ width: 30, height: 30, opacity: captain ? 0.35 : 1 }}
                      onClick={() => removeMember(team, member)}
                      disabled={captain || !!busy}
                      title={captain ? 'Сначала назначьте другого капитана' : 'Удалить из состава'}
                    >
                      <Ic name="x" size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </AdmCard>
        );
      })}
      {!loading && !teams.length && (
        <AdmCard style={{ padding: 28 }} className="text-center text-fg-muted">
          {onlyRegistered ? 'На этот турнир пока не подано ни одной командной заявки.' : 'В этой дисциплине пока нет команд.'}
        </AdmCard>
      )}
    </div>
  );
}

function LiveBracketAdmin({ tour }) {
  const user = useAppStore((s) => s.user);
  const startPolling = useMlbb((s) => s.startPolling);
  const online = useMlbb((s) => s.online);
  const activeTour = useMlbb((s) => s.tourId);
  const game = liveGame(tour);
  const isAdmin = user?.user_type === 'admin' || user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) return startPolling(tour.id, game);
  }, [tour.id, game, isAdmin, startPolling]);

  if (!isAdmin) return <AdmCard style={{ padding: 24 }}>Для управления сеткой нужны права администратора.</AdmCard>;

  return (
    <div className="flex flex-col gap-4">
    <AdmCard style={{ padding: 24 }} className="flex flex-col gap-3 items-start">
      <div className="flex items-center gap-2.5 text-fg" style={{ fontSize: 15 }}>
        <span style={{ color: '#c084fc', display: 'inline-flex' }}><Ic name="zap" size={18} /></span>
        <span className="font-display font-semibold uppercase">Управление матчами и сеткой</span>
      </div>
      <p className="text-fg-muted" style={{ fontSize: 13.5, lineHeight: 1.6, maxWidth: 620 }}>
        Выберите матч в сетке ниже, чтобы вручную изменить счёт, назначить победителя
        и перевести команду в следующий раунд или сбросить результат.
        Ручное управление доступно независимо от автоматического результата и чек-ина.
        Изменения сохраняются сразу и отображаются на странице турнира.
      </p>
      <a
        href={`/#tournament/${tour.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fed-btn-ghost h-10 px-4 rounded-[10px] gap-2 no-underline inline-flex items-center"
        style={{ fontSize: 13 }}
      >
        <Ic name="external" size={15} /> Открыть страницу турнира
      </a>
    </AdmCard>
      {!online && <p role="status" className="text-fg-muted">Нет связи с сервером. Для сохранения результатов требуется соединение.</p>}
      {activeTour === tour.id && (
        <MlbbBracket key={tour.id} tourId={tour.id} matchesOpen={false} M={{ team_you: 'Вы' }} userKey={null} onGoReg={() => {}} />
      )}
    </div>
  );
}

/* ---------- конструктор формы заявки ---------- */

const FIELD_TYPES = [
  ['text', 'Текст'],
  ['phone', 'Телефон'],
  ['select', 'Выбор из списка'],
  ['checkbox', 'Галочка'],
  ['file', 'Файл / документ'],
];

// Пресеты: семантичные ключи (phone/steam/…) — бэкенд и заявки их понимают
const FORM_PRESETS = {
  cs2: {
    label: 'CS2 · команда',
    fields: [
      { key: 'team', type: 'text', label_ru: 'Название команды', label_kz: 'Команда атауы', required: true },
      { key: 'roster', type: 'text', label_ru: 'Состав (5 основных + запасной)', label_kz: 'Құрам (5 негізгі + қосалқы)', required: true },
      { key: 'steam', type: 'text', label_ru: 'Steam-профиль капитана', label_kz: 'Капитанның Steam-профилі', required: true },
      { key: 'phone', type: 'phone', label_ru: 'Телефон капитана', label_kz: 'Капитан телефоны', required: true },
    ],
  },
  fifa: {
    label: 'FIFA · соло',
    fields: [
      { key: 'nick', type: 'text', label_ru: 'Игровой никнейм', label_kz: 'Ойын никнеймі', required: true },
      { key: 'phone', type: 'phone', label_ru: 'Телефон', label_kz: 'Телефон', required: true },
      { key: 'platform', type: 'select', label_ru: 'Платформа', label_kz: 'Платформа', required: false, options: ['PlayStation 5', 'Xbox', 'ПК'] },
    ],
  },
  edu: {
    label: 'Школьники / студенты',
    fields: [
      { key: 'nick', type: 'text', label_ru: 'Игровой никнейм', label_kz: 'Ойын никнеймі', required: true },
      { key: 'phone', type: 'phone', label_ru: 'Телефон', label_kz: 'Телефон', required: true },
      { key: 'edu_name', type: 'text', label_ru: 'Учебное заведение', label_kz: 'Оқу орны', required: true },
      { key: 'grade', type: 'text', label_ru: 'Класс / курс', label_kz: 'Сынып / курс', required: true },
      { key: 'edu_doc', type: 'file', label_ru: 'Справка с места учёбы', label_kz: 'Оқу орнынан анықтама', required: true },
    ],
  },
  min: {
    label: 'Минимум',
    fields: [
      { key: 'nick', type: 'text', label_ru: 'Никнейм', label_kz: 'Никнейм', required: true },
      { key: 'phone', type: 'phone', label_ru: 'Телефон', label_kz: 'Телефон', required: true },
    ],
  },
};

function FormTab({ draft, setDraft }) {
  const form = draft.form || { open: false, fields: [] };
  const setForm = (patch) => setDraft({ ...draft, form: { ...form, ...patch } });
  const fields = form.fields || [];
  const setFields = (f) => setForm({ fields: f });
  const updField = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setFields(next);
  };
  const applyPreset = (p) => {
    if (fields.length && !window.confirm('Заменить текущие поля формой из пресета?')) return;
    setFields(FORM_PRESETS[p].fields.map((f) => ({ ...f })));
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      <AdmCard style={{ padding: 18 }}>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.open}
            onChange={(e) => setForm({ open: e.target.checked })}
            style={{ width: 18, height: 18, accentColor: 'var(--fed-accent)' }}
          />
          <span className="font-display font-semibold uppercase text-fg" style={{ fontSize: 14 }}>Приём заявок открыт</span>
        </label>
        <p className="text-fg-muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Кнопка «Регистрация» на странице турнира откроет эту форму. Без полей или с закрытым приёмом заявки не принимаются.
        </p>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', marginTop: 14 }}>
          <label className="flex flex-col gap-[6px]">
            <span className="fed-label">Дедлайн приёма (необязательно)</span>
            <input
              className="fed-input"
              style={{ height: 40 }}
              type="date"
              value={form.deadline || ''}
              onChange={(e) => setForm({ deadline: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-[6px]">
            <span className="fed-label">Лимит заявок (0 — без лимита)</span>
            <input
              className="fed-input"
              style={{ height: 40 }}
              type="number"
              min={0}
              value={form.limit || 0}
              onChange={(e) => setForm({ limit: Math.max(0, parseInt(e.target.value || '0', 10)) })}
            />
          </label>
        </div>
      </AdmCard>

      <div className="flex gap-2 flex-wrap items-center">
        <span className="fed-label">Пресеты:</span>
        {Object.entries(FORM_PRESETS).map(([k, p]) => (
          <button
            key={k}
            onClick={() => applyPreset(k)}
            className="h-9 px-3.5 rounded-[9px] cursor-pointer font-display font-semibold uppercase text-fg-soft bg-surface-2"
            style={{ fontSize: 12, border: '1px solid var(--fed-line-2)' }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        {fields.map((f, i) => (
          <AdmCard key={f.key} style={{ padding: 14 }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
              <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{i + 1}</span>
              <select
                className="fed-input"
                style={{ height: 36, fontSize: 12.5, width: 170, flexShrink: 0 }}
                value={f.type}
                onChange={(e) => updField(i, { type: e.target.value })}
              >
                {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="flex items-center gap-1.5 cursor-pointer text-fg-soft" style={{ fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={!!f.required}
                  onChange={(e) => updField(i, { required: e.target.checked })}
                  style={{ width: 15, height: 15, accentColor: 'var(--fed-accent)' }}
                />
                обязательное
              </label>
              <span className="flex-1" />
              <button className="fed-iconbtn" title="Выше" onClick={() => move(i, -1)}>
                <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Ic name="chevD" size={16} /></span>
              </button>
              <button className="fed-iconbtn" title="Ниже" onClick={() => move(i, 1)}>
                <Ic name="chevD" size={16} />
              </button>
              <AdmIconBtn name="trash" tint="danger" title="Удалить поле" onClick={() => setFields(fields.filter((_, j) => j !== i))} />
            </div>
            <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))' }}>
              <input
                className="fed-input"
                style={{ height: 38, fontSize: 13 }}
                placeholder="Название поля (рус)"
                value={f.label_ru || ''}
                onChange={(e) => updField(i, { label_ru: e.target.value })}
              />
              <input
                className="fed-input"
                style={{ height: 38, fontSize: 13 }}
                placeholder="Атауы (қаз)"
                value={f.label_kz || ''}
                onChange={(e) => updField(i, { label_kz: e.target.value })}
              />
            </div>
            {f.type === 'select' && (
              <input
                className="fed-input"
                style={{ height: 38, fontSize: 13, marginTop: 10 }}
                placeholder="Варианты через запятую: PlayStation 5, Xbox, ПК"
                value={(f.options || []).join(', ')}
                onChange={(e) => updField(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
            )}
          </AdmCard>
        ))}
        {!fields.length && <div className="text-fg-faint" style={{ fontSize: 13 }}>Полей пока нет — выберите пресет или добавьте поле.</div>}
      </div>

      <button
        onClick={() => setFields([...fields, { key: 'f' + uid(), type: 'text', label_ru: '', label_kz: '', required: false }])}
        className="self-start h-10 px-4 rounded-[10px] cursor-pointer font-display font-semibold uppercase text-accent-bright"
        style={{ fontSize: 13, background: 'rgba(225,18,23,.08)', border: '1px solid var(--fed-line-accent)' }}
      >
        + Поле
      </button>
    </div>
  );
}

function GroupsTab({ draft, setDraft }) {
  const groups = draft.groups || [];
  const setGroups = (g) => setDraft({ ...draft, groups: g });
  const addGroup = () => {
    const letter = String.fromCharCode(65 + groups.length); // A, B, C…
    setGroups([...groups, { id: letter, teams: [] }]);
  };
  const addTeam = (gi) => {
    const name = window.prompt('Имя участника / команды:');
    if (!name) return;
    setGroups(groups.map((g, i) => (i === gi ? { ...g, teams: [...g.teams, name] } : g)));
  };
  return (
    <div className="fed-admin-view">
      {isDotaTournament(draft) && <GroupDraw tourId={draft.id} game={liveGame(draft)} groups={groups} onDraw={setGroups} />}
      {isDotaTournament(draft) && <div className="rounded-xl border border-line p-4 mb-4">
        <p className="text-fg-muted text-sm mb-3">Четыре группы по 4 или 5 команд · Double Elimination · два места в плей-офф. Заполните группы, сохраните турнир, затем откройте «Сетку».</p>
        <button type="button" className="fed-btn-primary px-4 py-2" onClick={() => {
          if (groups.length && !window.confirm('Создать четыре группы, сохранив команды первых четырёх групп?')) return;
          setGroups(Array.from({ length: 4 }, (_, index) => ({ id: String(index + 1), teams: groups[index]?.teams || [] })));
        }}>Создать 4 группы</button>
      </div>}
      <button
        onClick={addGroup}
        className="h-10 px-4 rounded-[10px] cursor-pointer font-display font-semibold uppercase text-accent-bright"
        style={{ fontSize: 13, background: 'rgba(225,18,23,.08)', border: '1px solid var(--fed-line-accent)', marginBottom: 16 }}
      >
        + Группа
      </button>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))' }}>
        {groups.map((g, gi) => (
          <AdmCard key={g.id} style={{ padding: 16 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <span className="font-display font-bold uppercase text-fg" style={{ fontSize: 15 }}>Группа {g.id}</span>
              <AdmIconBtn name="x" tint="danger" title="Удалить группу" onClick={() => setGroups(groups.filter((_, i) => i !== gi))} />
            </div>
            <div className="flex flex-col gap-1.5">
              {g.teams.map((t, ti) => (
                <div key={ti} className="flex items-center justify-between gap-2 bg-surface rounded-lg border border-line" style={{ padding: '7px 10px' }}>
                  <span className="text-fg-soft truncate" style={{ fontSize: 13 }}>{t}</span>
                  <button
                    onClick={() => setGroups(groups.map((gg, i) => (i === gi ? { ...gg, teams: gg.teams.filter((_, j) => j !== ti) } : gg)))}
                    className="bg-transparent border-none cursor-pointer text-fg-faint hover:text-accent-bright"
                    style={{ fontSize: 14, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => addTeam(gi)}
                className="rounded-lg cursor-pointer text-fg-muted bg-transparent"
                style={{ padding: '8px 10px', fontSize: 12.5, border: '1px dashed var(--fed-line-2)' }}
              >
                + участник
              </button>
            </div>
          </AdmCard>
        ))}
        {!groups.length && <div className="text-fg-faint" style={{ fontSize: 13 }}>Групп пока нет — добавьте первую.</div>}
      </div>
    </div>
  );
}

function BracketTab({ draft, setDraft }) {
  const rounds = draft.rounds || [];
  const setRounds = (r) => setDraft({ ...draft, rounds: r });
  const addRound = () => setRounds([...rounds, { title: `Стадия ${rounds.length + 1}`, matches: [] }]);
  const updRound = (ri, patch) => setRounds(rounds.map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const updMatch = (ri, mi, patch) => updRound(ri, { matches: rounds[ri].matches.map((m, j) => (j === mi ? { ...m, ...patch } : m)) });

  return (
    <div className="fed-admin-view">
      <button
        onClick={addRound}
        className="h-10 px-4 rounded-[10px] cursor-pointer font-display font-semibold uppercase text-accent-bright"
        style={{ fontSize: 13, background: 'rgba(225,18,23,.08)', border: '1px solid var(--fed-line-accent)', marginBottom: 16 }}
      >
        + Стадия
      </button>
      <div className="overflow-x-auto" style={{ paddingBottom: 8 }}>
        <div className="flex gap-4 items-start" style={{ width: 'max-content', minWidth: '100%' }}>
          {rounds.map((r, ri) => (
            <div key={ri} className="flex flex-col gap-2.5" style={{ minWidth: 230 }}>
              <div className="flex items-center gap-2">
                <input
                  className="fed-input"
                  style={{ height: 38, fontSize: 13 }}
                  value={r.title}
                  onChange={(e) => updRound(ri, { title: e.target.value })}
                />
                <AdmIconBtn name="x" tint="danger" title="Удалить стадию" onClick={() => setRounds(rounds.filter((_, i) => i !== ri))} />
              </div>
              {r.matches.map((m, mi) => (
                <AdmCard key={mi} style={{ padding: 10 }}>
                  {[['a', 'sa'], ['b', 'sb']].map(([nk, sk]) => (
                    <div key={nk} className="flex gap-1.5" style={{ marginBottom: nk === 'a' ? 6 : 0 }}>
                      <input
                        className="fed-input"
                        style={{ height: 34, fontSize: 12.5, flex: 1 }}
                        placeholder={nk === 'a' ? 'Участник 1' : 'Участник 2'}
                        value={m[nk] || ''}
                        onChange={(e) => updMatch(ri, mi, { [nk]: e.target.value })}
                      />
                      <input
                        className="fed-input text-center"
                        style={{ height: 34, fontSize: 12.5, width: 42, padding: 0 }}
                        placeholder="0"
                        value={m[sk] ?? ''}
                        onChange={(e) => updMatch(ri, mi, { [sk]: e.target.value })}
                      />
                    </div>
                  ))}
                </AdmCard>
              ))}
              <button
                onClick={() => updRound(ri, { matches: [...r.matches, { a: '', sa: '', b: '', sb: '' }] })}
                className="rounded-lg cursor-pointer text-fg-muted bg-transparent"
                style={{ padding: '8px 10px', fontSize: 12.5, border: '1px dashed var(--fed-line-2)' }}
              >
                + матч
              </button>
            </div>
          ))}
          {!rounds.length && <div className="text-fg-faint" style={{ fontSize: 13 }}>Стадий пока нет — добавьте первую.</div>}
        </div>
      </div>
    </div>
  );
}

function PlayersTab({ draft, setDraft }) {
  const [name, setName] = useState('');
  const players = draft.playersList || [];
  const setPlayers = (p) => setDraft({ ...draft, playersList: p });
  const add = () => {
    if (!name.trim()) return;
    setPlayers([...players, name.trim()]);
    setName('');
  };
  return (
    <div className="fed-admin-view">
      <div className="flex gap-2.5" style={{ marginBottom: 16, maxWidth: 420 }}>
        <input className="fed-input" style={{ height: 42 }} placeholder="Никнейм участника" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        <AdmPrimaryBtn onClick={add}>Добавить</AdmPrimaryBtn>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))' }}>
        {players.map((p, i) => (
          <div key={i} className="flex items-center gap-2.5 bg-surface-2 border border-line rounded-[10px]" style={{ padding: '9px 12px' }}>
            <span className="font-mono text-fg-faint" style={{ fontSize: 10 }}>{i + 1}</span>
            <span className="text-fg flex-1 truncate" style={{ fontSize: 13.5 }}>{p}</span>
            <button onClick={() => setPlayers(players.filter((_, j) => j !== i))} className="bg-transparent border-none cursor-pointer text-fg-faint hover:text-accent-bright" style={{ fontSize: 15, lineHeight: 1 }}>×</button>
          </div>
        ))}
        {!players.length && <div className="text-fg-faint" style={{ fontSize: 13 }}>Список участников пуст.</div>}
      </div>
    </div>
  );
}

export function AdmTournamentEditor({ tourId, onBack, onTeamsChanged }) {
  const tournaments = useFedContent((s) => s.tournaments);
  const saveTournament = useFedContent((s) => s.saveTournament);
  const fire = useAppStore((s) => s.fire);
  const src = tournaments.find((t) => t.id === tourId);
  const [draft, setDraft] = useState(() => src
    ? { ...src }
    : { id: 't' + uid(), title: '', disc: 'CS2', game: '', status: 'soon', date_ru: '', place_ru: '', format_ru: '', platform: '', prize_ru: '', players: 0, about_ru: '', hue: 8 });
  const [tab, setTab] = useState('info');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const live = isLive(draft);

  // Черновик локальный: на сервер уходит только по «Сохранить».
  const edit = (next) => { setDraft(next); setDirty(true); };

  const save = async () => {
    if (busy) return;
    if (!draft.title.trim()) { fire && fire('Укажите название турнира', 'error'); setTab('info'); return; }
    if (isDotaTournament(draft) && draft.groups?.length) {
      try { buildNovaBracket(draft.groups); }
      catch (error) { fire(error.message, 'error'); setTab('groups'); return; }
    }
    setBusy(true);
    const ok = await saveTournament({ ...draft, matchSchedule: src?.matchSchedule ?? draft.matchSchedule });
    setBusy(false);
    if (ok) { fire && fire('Турнир сохранён', 'ok'); onBack(); }
  };

  const cancel = () => {
    if (dirty && !window.confirm('Изменения не сохранены. Выйти?')) return;
    onBack();
  };

  const generateQr = async () => {
    if (busy || !src) return;
    const next = { ...draft, qrEnabled: true };
    setBusy(true);
    const ok = await saveTournament(next);
    setBusy(false);
    if (!ok) return;
    setDraft(next);
    setDirty(false);
    fire && fire(draft.qrEnabled ? 'QR-код открыт' : 'Постоянный QR-код создан', 'ok');
    setQrOpen(true);
  };

  const tabs = (live ? [
    { key: 'info', label: 'Информация', icon: 'info' },
    { key: 'live', label: 'Настройки', icon: 'zap' },
    { key: 'teams', label: 'Команды', icon: 'community' },
    ...((hasNovaGroups(draft) || isDotaTournament(draft)) ? [{ key: 'groups', label: 'Группы', icon: 'community' }] : []),
    { key: 'bracket', label: 'Сетка', icon: 'trophy' },
  ] : [
    { key: 'info', label: 'Информация', icon: 'info' },
    { key: 'form', label: 'Заявка', icon: 'inbox' },
    { key: 'groups', label: 'Группы', icon: 'community' },
    { key: 'bracket', label: 'Сетка', icon: 'trophy' },
    { key: 'players', label: 'Участники', icon: 'user' },
  ]).map((t) => ({ ...t, active: tab === t.key, onClick: () => setTab(t.key) }));

  return (
    <div className="fed-admin-view fed-tour-editor flex flex-col gap-5">
      <div className="fed-tour-editor-head flex items-center gap-3 flex-wrap">
        <button onClick={cancel} className="fed-tour-back bg-transparent border-none cursor-pointer text-fg-muted font-display font-semibold uppercase hover:text-fg" style={{ fontSize: 13.5, letterSpacing: '.04em' }}>
          ← К списку турниров
        </button>
        <span className="flex-1" />
        {dirty && <span className="font-mono uppercase text-gold" style={{ fontSize: 10.5, letterSpacing: '.1em' }}>Есть несохранённые правки</span>}
        {src && (
          <a href={`/#tournament/${draft.id}`} target="_blank" rel="noopener noreferrer" className="fed-tour-editor-view">
            <Ic name="external" size={17} /> Открыть турнир
          </a>
        )}
        {src && (
          <button type="button" onClick={generateQr} className="fed-tour-editor-view fed-tour-qr-admin" disabled={busy}>
            <Ic name="grid" size={17} /> {draft.qrEnabled ? 'Показать QR-код' : 'Сгенерировать QR-код'}
          </button>
        )}
        <button onClick={cancel} className="fed-tour-editor-action fed-btn-ghost h-10 px-5 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
        <AdmPrimaryBtn className="fed-tour-editor-action" icon="check" onClick={save}>{busy ? 'Сохраняем…' : 'Сохранить'}</AdmPrimaryBtn>
      </div>

      <Seg items={tabs} minWidth={110} height={40} className="fed-tour-tabs-desktop" />
      <div className="fed-tour-tabs-mobile" aria-label="Разделы турнира">
        {tabs.map((item) => (
          <button key={item.key} className={item.active ? 'on' : ''} onClick={item.onClick}>
            <Ic name={item.icon} size={18} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      {tab === 'info' && <InfoTab draft={draft} setDraft={edit} />}
      {tab === 'live' && <LiveTab draft={draft} setDraft={edit} />}
      {tab === 'teams' && live && <LiveTeamsAdmin tour={draft} onChanged={onTeamsChanged} />}
      {tab === 'form' && !live && <FormTab draft={draft} setDraft={edit} />}
      {tab === 'groups' && (!live || hasNovaGroups(draft) || isDotaTournament(draft)) && <GroupsTab draft={draft} setDraft={edit} />}
      {tab === 'bracket' && (hasNovaGroups(src || draft)
        ? <NovaBracket tour={src || draft} poll />
        : live ? <LiveBracketAdmin tour={draft} /> : <BracketTab draft={draft} setDraft={edit} />)}
      {tab === 'players' && !live && <PlayersTab draft={draft} setDraft={edit} />}
      <TournamentQrModal open={qrOpen} onClose={() => setQrOpen(false)} tourId={draft.id} title={draft.title} />
    </div>
  );
}
